import { Suspense, lazy, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, PackageSearch, Circle } from "lucide-react";
import { MagneticButton } from "./MagneticButton";
import { HERO_BG } from "../../data/content";
import { useI18n } from "../../context/LanguageContext";

const Scene3D = lazy(() => import("./Scene3D"));
const word = { hidden: { y: "110%" }, show: { y: 0 } };

export default function Hero() {
  const nav = useNavigate();
  const { t } = useI18n();
  const go = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  const lines = [t("hero.line1"), t("hero.line2")];

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const bgOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.55]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" data-testid="hero-section" ref={sectionRef} className="relative min-h-screen w-full overflow-hidden flex items-center">
      <motion.div style={{ scale: bgScale, opacity: bgOpacity }} className="absolute inset-0">
        <img src={HERO_BG} alt="Freight highway at night" className="w-full h-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
        <div className="absolute inset-0 light-streaks opacity-50" />
      </motion.div>

      <div className="absolute inset-0 z-[1] pointer-events-none opacity-90">
        <Suspense fallback={null}>
          <Scene3D />
        </Suspense>
      </div>

      {/* Floating live HUD cards beside the truck */}
      <div className="hidden lg:flex absolute right-10 top-1/2 -translate-y-1/2 z-[2] flex-col gap-4 pointer-events-none">
        {[
          { k: "MILES / YEAR", v: "48M+", s: "freight hauled" },
          { k: "ON-TIME", v: "99.2%", s: "delivery rate" },
          { k: "COVERAGE", v: "40+", s: "cities served" },
        ].map((c, i) => (
          <motion.div key={c.k} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2 + i * 0.15, duration: 0.7, ease: [0.33, 1, 0.68, 1] }}
            className="rounded-2xl px-5 py-4 w-52 animate-float bg-black/30 backdrop-blur-md border border-white/15" style={{ animationDelay: `${i * 0.9}s` }}>
            <div className="font-mono-x text-[10px] tracking-[0.3em] text-primary">{c.k}</div>
            <div className="font-heading font-extrabold text-3xl text-white mt-1">{c.v}</div>
            <div className="text-xs text-white/60">{c.s}</div>
          </motion.div>
        ))}
      </div>

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pointer-events-none">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="font-mono-x text-primary text-xs md:text-sm tracking-[0.35em] mb-6 flex items-center gap-3">
          <Circle className="w-2 h-2 fill-primary text-primary animate-pulse" /> {t("hero.eyebrow")}
        </motion.div>

        <h1 className="font-heading font-extrabold tracking-tighter leading-[0.85] text-white text-5xl sm:text-7xl lg:text-8xl">
          {lines.map((line, li) => (
            <span key={li} className="block overflow-hidden">
              <motion.span className="block" variants={word} initial="hidden" animate="show"
                transition={{ duration: 0.9, delay: 0.3 + li * 0.15, ease: [0.33, 1, 0.68, 1] }}>
                {li === 1 ? <span className="text-primary text-glow">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4 pointer-events-auto">
          <MagneticButton testId="hero-quote-button" onClick={() => go("#quote-form")}
            className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-8 py-4 text-sm hover:shadow-[0_0_40px_hsl(var(--primary)/0.5)] transition-shadow">
            {t("actions.quote")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </MagneticButton>
          <button data-testid="hero-track-button" onClick={() => nav("/track")}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-8 py-4 text-sm font-semibold backdrop-blur-sm hover:bg-white/10 transition-colors">
            <PackageSearch className="w-4 h-4" /> {t("actions.trackShipment")}
          </button>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 font-mono-x text-[10px] tracking-[0.4em] text-white/60 flex flex-col items-center gap-2">
        {t("hero.scroll")}
        <div className="w-px h-10 bg-gradient-to-b from-primary to-transparent" />
      </motion.div>
    </section>
  );
}
